import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { PRICING_TIERS } from '../lib/stripe';

export interface Subscription {
  tier: keyof typeof PRICING_TIERS;
  status: string;
  currentPeriodEnd: Date | null;
  isAdmin: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const fetchSubscription = async () => {
      try {
        // Check if user is admin
        const isAdmin = user.email === 'steve@shadowtek.com.au';

        // If admin, automatically grant lifetime access
        if (isAdmin) {
          setSubscription({
            tier: 'LIFETIME',
            status: 'active',
            currentPeriodEnd: new Date('2099-12-31'),
            isAdmin: true
          });
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data) {
          setSubscription({
            tier: 'FREE',
            status: 'active',
            currentPeriodEnd: null,
            isAdmin: false
          });
        } else {
          setSubscription({
            tier: data.plan_id === 'lifetime' ? 'LIFETIME' : 'FREE',
            status: data.status,
            currentPeriodEnd: data.current_period_end ? new Date(data.current_period_end) : null,
            isAdmin: false
          });
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        // Default to free tier on error
        setSubscription({
          tier: 'FREE',
          status: 'active',
          currentPeriodEnd: null,
          isAdmin: false
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  return { subscription, loading };
}